import { useNavigate } from "react-router-dom";
import LiteReportButton from "../components/LiteReportButton";
import { continueFullTitle, isFiveXType } from "../utils/participantAssessmentType";

export default function ContinueFullPage({
  fullSurveyToken,
  liteSurveyToken = null,
  liteReportAvailable = false,
  remainingQuestionCount = null,
  preSurveyPending = false,
  assessmentType,
  branding = null,
  onPreSurvey,
  onContinueFull,
}) {
  const navigate = useNavigate();
  const remaining =
    remainingQuestionCount != null && Number(remainingQuestionCount) >= 0
      ? Number(remainingQuestionCount)
      : null;
  const remainingMinutes =
    remaining != null
      ? Math.max(1, Math.round((remaining * 40) / 60))
      : null;
  const fiveX = isFiveXType(assessmentType);

  if (!fullSurveyToken) {
    return (
      <p className="p-8 text-center text-red-600">
        Full assessment link is not available.
      </p>
    );
  }

  return (
    <section className="min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 py-10 sm:py-14 bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <div className="max-w-2xl w-full">
        {branding?.logo_url ? (
          <div className="flex justify-center mb-4">
            <img src={branding.logo_url} alt="" className="h-8 w-auto max-w-[140px] object-contain" />
          </div>
        ) : null}
        <div className="text-center mb-8">
          <p className="text-sm font-semibold uppercase tracking-wide text-brand mb-2">
            Welcome back
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold text-brand mb-3">
            Continue the full assessment
          </h1>
          <p className="text-gray-700 text-sm sm:text-base leading-relaxed max-w-lg mx-auto">
            Pick up where you left off. Your LITE answers are already saved.
          </p>
        </div>

        <div className="flex flex-col gap-5">
          {preSurveyPending ? (
            <article className="rounded-2xl border border-[#004f73]/20 bg-gradient-to-br from-[#004f73]/5 via-white to-cyan-50/40 shadow-sm p-6 sm:p-7">
              <p className="text-xs font-bold uppercase tracking-wide text-[#004f73] mb-1">
                Recommended first
              </p>
              <h2 className="text-lg font-bold text-gray-900 mb-2">
                Complete your company benchmark
              </h2>
              <p className="text-sm text-gray-700 mb-4 leading-relaxed">
                Confirm company profile details before the full assessment, or skip and return later.
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-xl bg-[#004f73] px-6 py-3 text-sm font-semibold text-white hover:bg-[#003d5c]"
                  onClick={() => onPreSurvey?.()}
                >
                  Complete benchmark
                </button>
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-xl border border-[#004f73]/25 px-6 py-3 text-sm font-semibold text-[#004f73] hover:bg-[#004f73]/5"
                  onClick={() => {
                    if (onContinueFull) {
                      onContinueFull();
                      return;
                    }
                    navigate(`/a/${fullSurveyToken}`, { replace: true });
                  }}
                >
                  Skip — continue to assessment
                </button>
              </div>
            </article>
          ) : null}

          <article className="relative rounded-2xl border-2 border-brand bg-gradient-to-br from-brand/5 via-white to-brand/[0.07] shadow-lg shadow-brand/10 p-6 sm:p-8 text-center sm:text-left">
            <span className="absolute -top-3 left-6 inline-flex items-center rounded-full bg-brand px-3 py-1 text-xs font-bold uppercase tracking-wide text-white shadow-sm">
              Ready to continue
            </span>
            <h2 className="text-xl font-bold text-brand mt-2 mb-3">
              {continueFullTitle(assessmentType)}
            </h2>
            <p className="text-sm text-gray-700 leading-relaxed mb-6">
              {remaining != null ? (
                <>
                  Complete{" "}
                  <strong>
                    {remaining} remaining question{remaining === 1 ? "" : "s"}
                  </strong>
                  {remainingMinutes != null ? (
                    <> (~{remainingMinutes} min)</>
                  ) : null}{" "}
                  {fiveX
                    ? ` across the remaining pillars${remaining > 0 ? " and financial section" : ""}.`
                    : " across the remaining assessment areas."}
                </>
              ) : fiveX ? (
                <>
                  Complete the remaining questions across all pillars and the
                  financial section.
                </>
              ) : (
                <>Complete the remaining questions across the assessment.</>
              )}
            </p>
            <button
              type="button"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-brand px-8 py-3.5 text-base font-semibold text-white shadow-md hover:opacity-90"
              onClick={() => {
                if (onContinueFull) {
                  onContinueFull();
                  return;
                }
                navigate(`/a/${fullSurveyToken}`, { replace: true });
              }}
            >
              Continue with full assessment
            </button>
          </article>

          {liteReportAvailable && liteSurveyToken ? (
            <article className="rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50/80 p-6 sm:p-7 shadow-sm">
              <p className="text-xs font-bold uppercase tracking-wide text-amber-800 mb-1">
                Your LITE results
              </p>
              <h2 className="text-lg font-bold text-gray-900 mb-2">
                Check survey results here
              </h2>
              <p className="text-sm text-gray-700 mb-4">
                Review your LITE assessment report before you continue.
              </p>
              <LiteReportButton
                surveyToken={liteSurveyToken}
                label="View LITE assessment report"
                className="w-full sm:w-auto inline-flex items-center justify-center rounded-xl border-2 border-amber-400 bg-white px-6 py-3 text-sm font-semibold text-amber-900 hover:bg-amber-50 shadow-sm"
              />
            </article>
          ) : null}
        </div>
      </div>
    </section>
  );
}
